import * as React from "react";
import {
  AlertCircle,
  LayoutGrid,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { ZoneEditorSheet } from "@/components/seating/ZoneEditorSheet";
import {
  ZONE_ASSIGNMENT_LABELS,
  useZones,
  type Zone,
  type ZoneListFilters,
} from "@/lib/seating/zones";

/**
 * Phase F-finale-7 Slice 7b.4a — IA seat-management Zones surface.
 *
 * The first piece of the study-centre seat-management module: a
 * `<DataTable>` (Iron Rule #16) of the tenant's seating zones (floors /
 * rooms / sections) over the real-auth `/api/zones` surface, with create
 * + edit through `<ZoneEditorSheet>`. Seats (Slice 7b.4b) and bookings /
 * allocations (7b.4c/d) hang off these zones.
 *
 * Write affordances ("Add zone", the per-row edit action) are gated on
 * `useCanEditSeating` (`manage_zones`); without it the page is read-only
 * and shows the same view-only notice as the Slice 7b.1 config editor.
 * The BE re-checks `MANAGE_ZONES` on every call, so this is a UX layer,
 * not the only line of defence.
 */

const ACTIVE_FILTER_OPTIONS = [
  { value: "__any__", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
] as const;

type ActiveFilter = (typeof ACTIVE_FILTER_OPTIONS)[number]["value"];

type DialogState = { kind: "none" } | { kind: "create" } | { kind: "edit"; zone: Zone };

export function ZonesPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();

  const [qInput, setQInput] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState<ActiveFilter>("__any__");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "seating-zones",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  const filters = React.useMemo<ZoneListFilters>(
    () => ({
      is_active: activeFilter === "__any__" ? undefined : activeFilter === "active",
      limit: pageSize,
      offset: page * pageSize,
    }),
    [activeFilter, page, pageSize],
  );

  const query = useZones(filters);
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((z) =>
      [z.name, z.slug, z.description ?? ""].join(" ").toLowerCase().includes(needle),
    );
  }, [allItems, qInput]);

  const hasFiltersApplied = qInput !== "" || activeFilter !== "__any__";

  const clearFilters = React.useCallback(() => {
    setQInput("");
    setActiveFilter("__any__");
    setPage(0);
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Zones</PageHeaderHeading>
            <PageHeaderDescription>
              Floors, rooms, and sections members can be seated in. Seats and bookings are organised
              under these zones.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so zones are read-only. Ask an administrator to grant it if you need to make changes.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load zones
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search zones…"
              className="pl-8"
              aria-label="Search zones"
            />
          </div>
          <Select
            value={activeFilter}
            onValueChange={(v) => {
              setActiveFilter(v as ActiveFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
          {canEditSeating ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="zones-add-button"
            >
              <LayoutGrid aria-hidden="true" className="size-4" />
              Add zone
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Name</TableHead>
                <TableHead className="w-[12%] text-right">Capacity</TableHead>
                <TableHead className="w-[32%]">Facilities</TableHead>
                <TableHead className="w-[10%]">Status</TableHead>
                <TableHead className="w-[6%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={5} />
            ) : (
              <TableBody>
                {filteredItems.map((z) => (
                  <TableRow key={z.id} data-testid={`zone-row-${z.id}`}>
                    <TableCell className="font-medium">
                      <div>{z.name}</div>
                      <div className="text-muted-foreground text-xs">
                        {ZONE_ASSIGNMENT_LABELS[z.assignment_mode ?? "inherit"]}
                      </div>
                      {z.description ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {z.description}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {z.capacity > 0 ? z.capacity.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      {z.facilities.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {z.facilities.slice(0, 3).map((f) => (
                            <Badge key={f} variant="outline" className="text-[10px]">
                              {f}
                            </Badge>
                          ))}
                          {z.facilities.length > 3 ? (
                            <span className="text-muted-foreground text-xs">
                              +{z.facilities.length - 3}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={z.is_active ? "active" : "inactive"}
                        label={z.is_active ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {canEditSeating ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${z.name}`}
                              data-testid={`zone-row-actions-${z.id}`}
                              className="size-7"
                            >
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onSelect={() => setDialog({ kind: "edit", zone: z })}
                              data-testid={`zone-action-edit-${z.id}`}
                            >
                              <Pencil aria-hidden="true" className="size-4" />
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<LayoutGrid aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : "No zones yet"}
            description={
              hasFiltersApplied
                ? "Adjust filters or clear them to see every zone."
                : "Create zones (floors, rooms, sections) to organise seats and bookings."
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canEditSeating ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <LayoutGrid aria-hidden="true" className="size-4" />
                  Add zone
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="zones-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canEditSeating ? (
        <ZoneEditorSheet
          open={dialog.kind === "create" || dialog.kind === "edit"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          existing={dialog.kind === "edit" ? dialog.zone : null}
          takenSlugs={allItems.map((z) => z.slug)}
          onSaved={() => setDialog({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}
